<template>
  <div class="error-message" :class="`error-message--${type}`">
    <div class="error-message__icon" :aria-hidden="true">{{ errorIcon }}</div>
    <div class="error-message__content">
      <h4 class="error-message__title">{{ title }}</h4>
      <p class="error-message__description">{{ description }}</p>
      <button 
        v-if="showRetry"
        @click="$emit('retry')"
        class="error-message__retry-button"
        type="button"
      >
        {{ retryText }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ErrorMessage',
  props: {
    type: {
      type: String,
      default: 'error',
      validator: (value) => ['error', 'warning', 'info'].includes(value)
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    showRetry: {
      type: Boolean,
      default: false
    },
    retryText: {
      type: String,
      default: 'Try Again'
    }
  },
  emits: ['retry'],
  computed: {
    errorIcon() {
      const icons = {
        error: '⚠️',
        warning: '⚠️',
        info: 'ℹ️'
      }
      return icons[this.type] || '⚠️'
    }
  }
}
</script>

<style scoped>
.error-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  border: 1px solid transparent;
}

.error-message--error {
  background-color: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}

.error-message--warning {
  background-color: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.3);
  color: #f59e0b;
}

.error-message--info {
  background-color: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.error-message__icon {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.error-message__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.error-message__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: inherit;
}

.error-message__description {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  opacity: 0.9;
}

.error-message__retry-button {
  align-self: flex-start;
  padding: 8px 16px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.error-message__retry-button:hover {
  background-color: currentColor;
  color: white;
}

.error-message__retry-button:focus {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .error-message {
    border-width: 2px;
  }
  
  .error-message--error {
    background-color: rgba(239, 68, 68, 0.2);
    color: #dc2626;
  }
  
  .error-message--warning {
    background-color: rgba(245, 158, 11, 0.2);
    color: #d97706;
  }
  
  .error-message--info {
    background-color: rgba(59, 130, 246, 0.2);
    color: #2563eb;
  }
}
</style>